// RECORD MONEY v2 — batch 3's rebuild of RecordMoney (UI rebuild; drawn when lib/ui-v2.ts switches batch 3 on).
//
// Astra's record / expense / capital / drawing mockups, under the operator's ruling of 26 Oct 2026 (Astra's shape and
// content are the default): each mode is its own focused page with its own title — no mode chips; the weekly modes are
// opened from New entry, and every route and ?mode= link lands where it does today (/record, /record/expense, /capital,
// /drawing, ?mode=p2p|profit_share|charge|expense|capital|drawing|customer, and ?mode= changing on a mounted screen).
// Record money is the party's money in / out: direction, Party | Amount, Asset | Account, the party's balance (beside
// the form from lg, under the party on the phone), More (As of, the fee on top, Notes), then Review entry → the
// readback → the post. The Last 10 is back beside the form (LastTen, restored 1 Nov 2026); on the phone one "Last 10 →" link.
//
// THE STATE MACHINE IS screens/RecordMoney.tsx's, COPIED VERBATIM (the component body below, line for line, from its
// first hook to its Retry helper): every query and key, readState on the parties / balances / accounts / box /
// settlements, the rules that take the asset off the account, the held reference (refRef) and the confirmations it carries (confirmedRef),
// retireReference, the repeat guard, the new-account door, the dating line, the money-without-a-trade line, the post's
// body, its disabled-until rule and its button words. Added: `review` (layout state — the readback is open) and its
// close on success. The helpers it uses that RecordMoney does not export (tradeHref, About, the new-account refusal
// readers) are copied below; what posted is drawn by the shared Receipt (components/v2/Receipt).
import { useState, useMemo, useEffect, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { ArrowDownLeft, ArrowUpRight } from 'lucide-react';
import { DeskSwitch, DESK_LABEL, type Desk } from '../../components/DeskSwitch';
import { isSentinel, nothingLanded, ApiError, readWords } from '../../lib/errors';
import { api, EXPENSE_CATEGORIES } from '../../lib/api';
import { tg } from '../../lib/telegram';
import {
  ASSETS, type AssetCode, treasuryPath, partyPath, makeReference,
  isFiat, DEFAULT_RAILS,
} from '../../lib/workflow';
import { Combobox, type ComboboxOption } from '../../components/Combobox';
import { rosterCut, usePartySearch } from '../../lib/roster-cut';
import { RailPicker, type RailValue } from '../../components/RailPicker';
import { RepeatPanel, useRepeatGuard, type RepeatKind } from '../../components/RepeatPanel';
import { AsOfChips } from '../../components/AsOfChips';
import { Field } from '../../components/Field';
import { boxWords } from '../../components/BoxLines';
import { BalanceRows } from '../../components/v2/record/BalanceRows';
import { ErrorLine } from '../../components/ErrorLine';
import { AccountsDown } from '../../components/kit';
import { Notice, PageHead, StateLine, ServerLine, TAP } from '../../components/v2/kit';
import { Receipt, ReceiptAmount, postedAsOf } from '../../components/v2/Receipt';
import { amountOk, amountProblem, optionalAmountProblem } from '../../lib/amount';
import { partyName, partySubtitle, isStale, needsNameReview, rankForQuery, railName, sameAsset, foldAsset, partyBoxWords, dustFor, partyBalanceOf } from '../../lib/naming';
import { displayAsset, fmtAmount, fmtDay, fmtTime, dubaiWallToIso, backdateProblem, isoToDubaiWall } from '../../lib/format';
import { readState } from '../../lib/read-state';
import { MoreDetails } from '../../components/MoreDetails';
import { RECORD_KIND_COPY } from '../RecordExpense';
import { type Posted, type RecordMode } from '../RecordMoney';
import { RecordCharge } from '../RecordCharge';
import { BetweenPartiesV2 } from '../../components/v2/record/BetweenParties';
import { ProfitShareV2 } from '../../components/v2/record/ProfitShare';
import { OwnerFormV2 } from '../../components/v2/record/OwnerForm';
import { AboutFold, AsideBlock, FormFoot, FormPage, ReviewButton, ReviewPanel } from '../../components/v2/record/parts';
import { LastTen, LastTenLink, type LastTenProps } from '../../components/v2/record/LastTen';

type Direction = 'in' | 'out';

// The ?mode= contract (RecordMoney's MODES, values only — the chips are gone).
const MODE_VALUES: readonly RecordMode[] = ['customer', 'expense', 'capital', 'drawing', 'profit_share', 'charge', 'p2p'];
const isRecordMode = (v: unknown): v is RecordMode => MODE_VALUES.some((m) => m === v);

/** Each mode's page title — lib/routes.ts's names. */
const MODE_TITLE: Record<Exclude<RecordMode, keyof typeof RECORD_KIND_COPY>, string> = {
  customer: 'Record money', p2p: 'Between parties', profit_share: 'Profit share', charge: 'Record delivery charge',
};

/** RecordMoney's tradeHref, copied: a Book-trade link carrying this movement's facts (party, the direction it implies,
 *  the amount on the side the party paid or received, the rail on its side, the instant, settle-later ticked). */
function tradeHref(x: { party: string; direction: Direction; asset: string; amount: string; when: string; rail: string; later: boolean }): string {
  const q = new URLSearchParams();
  q.set('party', x.party);
  const coin = !isFiat(x.asset);
  q.set('dir', (x.direction === 'out') === coin ? 'customer_buy' : 'customer_sell');
  const side = x.direction === 'in' ? 'take' : 'give';
  q.set(side, x.amount);
  if (x.rail) { q.set('rail', x.rail); q.set('railAsset', x.asset); q.set('railSide', side); }
  if (x.when) q.set('when', x.when);
  if (x.later) q.set('later', '1');
  return `/trade?${q.toString()}`;
}

/** RecordMoney's "About this screen" per mode, copied (the explanation, behind a tap). */
/** "About this screen" per mode — every sentence that used to sit in the
 *  form (§7 rule 6). Short paragraphs, the operator's words. The expense,
 *  capital and drawing texts are RecordExpense's (it owns that copy). */
function About({ mode }: { mode: RecordMode }) {
  if (mode === 'p2p') {
    return (
      <>
        <p>
          One party&rsquo;s balance moves to another&rsquo;s. No desk account moves and the desk earns
          nothing. &ldquo;And in return&rdquo; adds a leg going back, which makes it a swap between the two
          at their own terms.
        </p>
        <p>Any two of the two amounts and the rate fill the third.</p>
        <p>
          The two parties can be on different desks. The swap is still one entry: each leg carries its
          own pair between the desks, so both desks&rsquo; books tie.
        </p>
      </>
    );
  }
  if (mode === 'profit_share') {
    return (
      <>
        <p>
          The desk&rsquo;s share of a sub-broker&rsquo;s balance: the party&rsquo;s balance falls by the
          amount and the sub-broker cost line falls by the same, so profit rises. No cash moves and no
          desk account is touched — the cash half is recorded as Money out on its own.
        </p>
      </>
    );
  }
  if (mode === 'charge') {
    return (
      <>
        <p>
          A charge for bringing the money or the cheque to the customer. It is its own entry and goes onto
          its own profit line, Delivery charges — the trade or the cheque it belongs to is left as it is.
        </p>
        <p>
          Paid now puts the money into the account you pick. On their account means they owe it and nothing
          moves yet.
        </p>
        <p>
          Taken in a coin, it is counted in AED at the rate you type, and the coins are bought into the
          desk&rsquo;s stock at that rate — three entries, recorded in one go.
        </p>
      </>
    );
  }
  if (mode !== 'customer') return RECORD_KIND_COPY[mode].about;
  return (
    <>
      <p>
        Money in or out against a party, without a trade — a deposit before the coins go, a payment
        after they came. Both sides post together: the party&rsquo;s box and the account the money
        went through.
      </p>
      <p>
        <strong className="font-medium text-tg-text">As of</strong> is when the money moved. Now is
        the default; pick Yesterday or a time when you are writing down money that moved earlier —
        the reference carries that day, and a back-dated trade settles from money recorded around the
        same time.
      </p>
      <p>
        An account that has never held this asset asks once before opening it. A fee on top of a
        payment — a network fee, a commission, a courier charge — goes under More: the party gets the
        amount, the account pays the amount plus the fee, and the fee lands in profit.
      </p>
    </>
  );
}

export function RecordV2({ initialMode = 'customer' }: { initialMode?: RecordMode } = {}) {
  const nav = useNavigate();

  // Routes (/record/expense, …) and the ⌘K palette land on a mode directly;
  // the selector still switches freely from there.
  // Arriving from a party page: ?party=<slug>&dir=in|out prefills who and which
  // way, so the operator does not retype a party they were already looking at.
  // The links used to carry ?party= and this screen ignored it entirely.
  const [search] = useSearchParams();
  const fromParty = search.get('party') ?? '';
  const fromDir   = search.get('dir') === 'out' ? 'out' : 'in';

  // ?mode=p2p (the party page's "Move to another party", the palette) lands on
  // a mode the way the /record/<mode> routes do — and keeps landing if the URL
  // changes while this screen is already mounted, since /record is one route.
  const modeParam = search.get('mode');
  const [mode, setMode] = useState<RecordMode>(isRecordMode(modeParam) ? modeParam : initialMode);
  useEffect(() => { if (isRecordMode(modeParam)) setMode(modeParam); }, [modeParam]);
  const [direction, setDirection] = useState<Direction>(fromDir as Direction);
  // ?asset= / ?rail= from a Chain row — the movement's own asset and wallet,
  // not the AED-cash default (operator, 1 Sept: book straight from the chain).
  const [asset, setAsset]         = useState<AssetCode>((search.get('asset') as AssetCode) || 'AED');
  // EXTERNAL RETIRED (operator, 2 Sept: "I highly doubt that button is ever
  // needed... for expenses we have a separate mechanism, and for capital in we
  // have one"). Every no-party flow already has its own honest door — Expense,
  // Capital in, Drawing, Move — and what remained was 'money vanished,
  // unclassified', the exact entry the closed books exist to prevent. Party
  // mode therefore ALWAYS has a party on the other side.
  const [slug, setSlug]           = useState(fromParty);
  const [amount, setAmount]       = useState(search.get('amount') ?? '');
  const [notes, setNotes]         = useState(search.get('note') ?? '');
  // Party movements must move a RAIL too — see the submit block for why.
  //
  // A LINK THAT NAMES THE ASSET MUST NOT LAND ON OFFICE CASH. Settlements'
  // Receive / Pay buttons carry ?asset= and ?amount= from the party's box but
  // no rail, because only the operator knows which pocket the money moved
  // through. Defaulting to cash.office answered that question for them: a USDT
  // leg opened on the AED cash rail, the red "that rail holds AED" line was
  // already on screen before anything was touched, and the asset box showed AED
  // while the state — and the posting — still said USDT. Start with no rail
  // instead, so the picker asks for the account and the operator answers.
  const [counterRail, setCounterRail] = useState<RailValue>(() => {
    const rail = search.get('rail'), railAsset = search.get('railAsset');
    if (rail && railAsset) return { slug: rail, asset: railAsset };
    if (search.get('asset')) return { slug: '', asset: '' };
    return { ...DEFAULT_RAILS.cash };
  });
  // ?cost= & ?costCategory= — "Book it again" after reversing a payment that
  // carried a fee on top hands the fee back separately (S7, 21 Sept 2026).
  const [costAmount, setCostAmount]   = useState(() => {
    const c = Number(search.get('cost')); return Number.isFinite(c) && c > 0 ? String(c) : '';
  });
  const [costCategory, setCostCategory] = useState(() => {
    const c = search.get('costCategory');
    return c && EXPENSE_CATEGORIES.some((x) => x.value === c) ? c : 'other';
  });

  // ── WHEN DID THIS HAPPEN? (5 Sept 2026) ───────────────────────────────────
  // The other half of the Loki 3 Sept case (see NewTrade): a back-dated trade
  // is settled by the sweep from money recorded around the same time, so money
  // written down late must be able to carry the time it really moved. Empty
  // means now. The value is Dubai wall-clock, read through lib/format.ts — a
  // datetime-local carries no zone, and the books have one.
  // ?when=2026-09-11T10:02 (Dubai wall-clock) rides in from a Chain row — the
  // block time is when the money actually moved.
  const [when, setWhen] = useState(search.get('when') ?? '');
  // The time a Chain row (or a Record money link) sent along: shown as its own chip.
  const urlWhen = search.get('when') ?? '';
  const whenIso   = when ? dubaiWallToIso(when) : '';
  const whenWords = backdateProblem(when);
  // WHAT THE LAST POSTING SAID, kept apart from the live field (review, 5 Sept
  // 2026 — the same fault as NewTrade). The success line read `whenIso` and
  // nothing cleared `when` on success, so the next movement typed straight
  // after a back-dated one went out on that earlier day too. The date now
  // leaves with the entry it was typed for (onSuccess), and this holds what
  // was actually POSTED: the instant, the reference, and whether the BFF
  // deduped it — a retry whose first attempt had landed keeps that attempt's
  // date, whatever this request said.
  const [posted, setPosted] = useState<Posted | null>(null);
  // The readback is open (Astra: Review entry before the post). Layout state only — the post is the same mutation.
  const [review, setReview] = useState(false);

  // Rail balances drive the picker for party movements — the operator has to
  // say which pocket the money actually moved through.
  // WHICH DESK'S ACCOUNT the money moved through. The party stays a DubaiOTC
  // party; the account may be an India bank (6 Sept 2026: "how do I pay INR
  // from RBL to Loki OTC" — the picker only ever listed Dubai's rails, so the
  // operator had typed "rbl" into the Customer box). A cross-desk posting is
  // one transaction, rail on one desk and party on the other; the books hold
  // both desks under one roof and the inter-desk bridge is a pending item
  // (registry §2, Inter-entity).
  const [railDesk, setRailDesk] = useState<Desk>('dubaiotc');
  const treasuryRows = useQuery({
    queryKey: ['treasury', 'balances', railDesk],
    queryFn:  () => api.treasuryBalances(railDesk),
    // The asset list below is derived from what the chosen rail actually holds.
    enabled:  mode === 'customer',
  });
  // What the account list is worth — `readState` (lib/read-state.ts; Appendix B): 'known' only once a read SUCCEEDED; a failed refresh keeps its
  // old rows beside the error, a retry of a failure and an offline pause have none. A guard needs 'known'; a banner says 'failed' / 'waiting'.
  const accounts = readState(treasuryRows);
  // No list at all because the read FAILED (a failed REFRESH keeps its rows: that picker stays and is told, readFailed): an open picker would say "No such account" and point at New party.
  const accountsDown = accounts === 'failed' && !treasuryRows.data;

  // ── WHAT DOES THE CHOSEN RAIL ACTUALLY HOLD? ────────────────────────────
  //
  // The asset used to be a free dropdown standing next to the rail, defaulted
  // to AED. On 2026-08-27 that put 4,500,000 AED onto the INR Angadiya rail and
  // minted `cash:angadiya:fiat:aed` — a rail that had never existed, holding
  // the wrong currency beside the real one.
  //
  // RailPicker has ALWAYS returned {slug, asset} together — the screen was
  // throwing the asset away and trusting its own dropdown. Now the rail decides,
  // and the dropdown only offers what that rail carries. Balance, not history,
  // for the same reason the BFF guard uses balance: a reverted phantom leaves
  // volumes behind forever and would re-offer the wrong asset.
  const railSlug = counterRail.slug;
  const railAssets = useMemo(() => {
    // What an account HOLDS is read off its balance. A failed REFRESH keeps the old rows beside the error, and a retry has none (design
    // system Appendix B): the holding is unknown then — the dropdown offers the full list and no "That account holds …" is said from them.
    if (accounts !== 'known') return [];
    const rows = treasuryRows.data?.rows ?? [];
    const held = rows
      .filter((r) => r.slug === railSlug && Number(r.balance) !== 0)
      .map((r) => r.asset as AssetCode);
    return [...new Set(held)];
  }, [treasuryRows.data, accounts, railSlug]);
  // No rail chosen, or a brand-new one: fall back to the full list rather than
  // an empty dropdown the operator cannot get out of.
  //
  // THE BOX ALWAYS OFFERS WHAT IT IS HOLDING. A <select> whose value is not
  // among its options renders the FIRST option and keeps the other value in
  // state — the box says AED and the posting says USDT. The folded code a
  // party's box speaks ('USDT') is not in ASSETS at all, so a Settlements link
  // arrives holding exactly such a value. Keep it listed until a rail replaces
  // it, and the two can never disagree.
  const assetChoices: AssetCode[] = useMemo(() => {
    const base = railAssets.length ? railAssets : [...ASSETS];
    return base.some((a) => a === asset) ? base : [asset, ...base];   // dialect-ok: this asks what the option list literally holds, not whether two codes mean the same coin
  }, [railAssets, asset]);
  // sameAsset, never includes(): the rail speaks the v3 fold (USDT) while the
  // dropdown may speak the chain form (USDT·TRC) — the guard fired on the SAME
  // dollar and refused honest postings (operator hit it 2 Sept).
  const assetMismatch = railAssets.length > 0 && !railAssets.some((a) => sameAsset(a, asset));
  // SNAP THE INCOMING CODE ONTO THE OPTION THAT IS SHOWN (operator, 3 Sept: a
  // Chain chip for a 56,426 USDT·ERC receipt opened this screen reading ETH).
  // The chip speaks the chain code (USDTERC), the rail's list speaks the fold
  // (USDT); a <select> holding a value it cannot display shows its first
  // option instead. Same dollar, one spelling — the list's.
  useEffect(() => {
    if (railAssets.length === 0) return;
    const exact = railAssets.some((a) => a === asset);   // dialect-ok: exact option match; the fold is checked right below
    if (exact) return;
    const same = railAssets.find((a) => sameAsset(a, asset));
    if (same) setAsset(same);
  }, [railAssets, asset]);

  // ── party options ───────────────────────────────────────────────────────
  const parties = usePartySearch({
    base: ['parties', 'dubaiotc'], text: slug,
    ask: (q) => api.listParties({ entity: 'dubaiotc', q, with_balances: true }),
    enabled: mode === 'customer' && slug.length > 0,
  });
  // The selected party's TYPE decides which account its leg posts to. Without
  // it every merchant desk gets a second, parallel account — see partyPath().
  const selectedParty = useMemo(
    () => (parties.data?.parties ?? []).find((p) => p.slug === slug),
    [parties.data, slug],
  );
  const selectedPartyType = selectedParty?.party_type;
  // WHAT THE LIST IS WORTH — `readState` (lib/read-state.ts; Appendix B): the names, and the balances (a list the BFF flags
  // `balances_read: false` has the names and not the balances: its empty balances are not "square"). The picker's box words take the
  // balances' answer.
  const lookup = readState(parties);
  const listBalances = readState(parties, { balances: true });
  const listBalancesRead = listBalances === 'known';
  // That unread list says so once, with a Retry, whatever is typed (one query per search; the flag is read off the list showing).
  const listBalancesFlagged = lookup === 'known' && listBalances === 'failed';

  const partyOptions: ComboboxOption[] = useMemo(() =>
    // OWN ACCOUNTS ARE NOT CUSTOMERS. Every rail carries a party record so the
    // Parties screen can show it — `rail-pr-hk` IS `cash.angadiya`. Offered
    // here, picking one posts to `customer:dubaiotc:rail-pr-hk`: a phantom party
    // holding money beside the real rail, and the rail itself never moves.
    // NewTrade has always filtered these; this screen never did (found by the
    // operator 2026-08-27, when the Customer box auto-completed to `rail-pr-hk`).
    rankForQuery((parties.data?.parties ?? []).filter((p) => p.party_type !== 'own_account'), slug).map((p) => ({
      value:     p.slug,
      label:     partyName(p),
      secondary: [partySubtitle(p, { compact: true }), listBalancesRead ? partyBoxWords(p) : ''].filter(Boolean).join(' · '),
      stale:     isStale(p),
      review:    needsNameReview(p.display_name),
    }))
  , [parties.data, listBalancesRead, slug]);

  // ── BOTH SIDES, BEFORE AND AFTER ────────────────────────────────────────
  // An entry moves two accounts and the screen showed only one of them: the
  // rail's balance sat in the picker, the party's was nowhere. So "does this
  // leave HK square?" meant leaving the screen to find out.
  const partyExists = !!selectedParty;
  /** The Last 10 of this direction, narrowed to the party once one is known (RecentEntries' read, restored 1 Nov 2026). */
  const lastTen: LastTenProps = { kind: 'money', direction, partySlug: partyExists ? slug : undefined };
  const partyBalances = useQuery({
    queryKey: ['party', slug, 'balances'],
    queryFn:  () => api.partyDetail(slug, 'dubaiotc'),
    enabled:  partyExists,
    staleTime: 30_000,
  });
  // The party's box is drawn — and anything is said from it — only once a read SUCCEEDED (see lib/read-state.ts).
  const box = readState(partyBalances);
  const amountN   = Number(amount) || 0;
  const costN     = Number(costAmount) || 0;
  // WHAT WAS TYPED, CHECKED BEFORE IT IS SENT. '1,000' is NaN, reaches the API
  // as null and comes back as a 500 about decimal strings; a minus sign posts
  // and runs the entry backwards. One rule for every amount box (lib/amount.ts).
  const amountWords = amountProblem(amount);
  const costWords   = optionalAmountProblem(costAmount);
  // Sign law: money IN means the party paid the desk — their tracker falls.
  // fold-probe: party balances arrive FOLDED from the BFF (stablecoins
  // netted under 'USDT'), the screen's asset may be chain-suffixed.
  const partyNow  = partyBalanceOf(partyBalances.data?.balances, asset);
  // THE FEE IS ON TOP (operator, 17 Sept 2026: "fees box seems wrong, amount
  // is getting deducted instead of adding"). The Amount box is what the PARTY
  // gets; the cost is what the account pays over and above it — the same
  // reading as Move money's "sender pays" and the venue withdrawals, where
  // the fee is charged on top of the coins sent. party_payment_split.num
  // still sends the cost to the expense account and the rest to the party,
  // so the account is debited amount + cost. Before this the cost was taken
  // OUT of the amount and Abdellah's 6.289 payout credited him 6.28898076.
  const toPartyN  = amountN;
  const railDebitN = direction === 'out' ? Number((amountN + costN).toFixed(8)) : amountN;
  const partyNext = partyNow + (direction === 'in' ? -amountN : toPartyN);

  // ── MONEY WITHOUT A TRADE (sweep 3.1 #6 / item 29, 14 Sept 2026) ──────────
  // Coins moved to or from a MERCHANT and no trade explained them: N40K3 was
  // booked two days after its money, 6KB2I eight days after, and HK's
  // 1,00,000 USDT of 8 Sept still had no trade on the 13th. This screen never
  // asked what a movement was for. Now, after a posting to or from a
  // merchant, it reads the box back and — when the box carries a coin
  // position with no awaiting trade in that asset — says so in one line,
  // with Book trade prefilled. Read from what was POSTED and from the two
  // re-reads the global refresh makes after every posting (the party's box,
  // the settle-later list — the one read that says which assets still have a
  // trade open), never from the live fields. Nothing automatic: a sentence
  // and a link.
  // READ ONCE A PARTY IS KNOWN (sweep item 23, 15 Sept 2026): the same list
  // feeds the dating line below, which has to speak BEFORE the posting —
  // so the read is no longer held back until a merchant coin movement has
  // posted. One key, one cache entry: the after-posting re-read is the
  // global refresh's, as before.
  // Is the open-trades check WANTED (the same conditions as ever: a party picked, or a coin movement just posted to a merchant)? When it is
  // and its read failed, the line below says it was not done — a check that could not run found nothing to say, but did not say so.
  const settlementsWanted = partyExists || (!!posted && posted.merchant && !isFiat(posted.asset));
  const settlements = useQuery({
    queryKey: ['settlements'],
    queryFn:  () => api.settlements(),
    enabled:  settlementsWanted,
    staleTime: 15_000,
  });
  const settlementsRead = readState(settlements);
  const nudge = useMemo(() => {
    if (!posted || posted.deduped || !posted.merchant || isFiat(posted.asset)) return null;
    if (posted.slug !== slug) return null;   // the box on screen is somebody else's now
    // Both reads must be NEWER than the posting: the box and the open list
    // with this movement in them, not the ones the entry was typed against.
    if (!partyBalances.data || partyBalances.dataUpdatedAt <= posted.at) return null;
    if (!settlements.data || settlements.dataUpdatedAt <= posted.at) return null;
    // A failed refresh keeps the old rows beside the error, and a retry has none: "no trade behind it" is said only from two reads that SUCCEEDED.
    if (box !== 'known' || settlementsRead !== 'known') return null;
    const held = partyBalanceOf(partyBalances.data.balances, posted.asset);
    if (Math.abs(held) <= dustFor(posted.asset)) return null;
    const open = settlements.data.parties.find((p) => p.slug === posted.slug);
    if (open?.open_assets.some((a) => sameAsset(a, posted.asset))) return null;
    return { held, name: partyName(partyBalances.data.party) };
  }, [posted, slug, partyBalances.data, partyBalances.dataUpdatedAt, box, settlements.data, settlements.dataUpdatedAt, settlementsRead]);

  // ── THE DATING LINE (sweep §4.3 ix / item 23, 15 Sept 2026) ──────────────
  // Money written down with As of left on Now, when it actually moved
  // yesterday, carries the wrong day: the reference and the statement say
  // today, and the sweep's pool — which opens a day before the oldest
  // awaiting trade and runs forward — still finds it, but the desk's own
  // record of WHEN it moved is false (Loki, 3 Sept: the trade was typed two
  // days late and the dates never lined up again). So, while the chips say
  // Now and the party picked has a settle-later trade booked more than 12 h
  // ago with an open leg in the asset being recorded, this side of it (money
  // in is the take leg — what they pay; money out the give leg — what they
  // get), one amber line names the trade's day and offers to set As of to
  // its instant. A nudge, not a gate: Record stays enabled, nothing is set
  // without the tap. The oldest such trade is named — money recorded at its
  // instant is inside every later trade's window too. A trade dated before
  // the books opened is left alone: As of could not be set to it anyway
  // (backdateProblem), and the sweep never draws for one.
  const dating = useMemo(() => {
    if (!selectedParty || when !== '' || settlementsRead !== 'known' || !settlements.data) return null;   // a failed refresh keeps the old list, a retry has none: no nudge from either
    const open = settlements.data.parties.find((p) => p.slug === slug);
    if (!open) return null;
    const side = direction === 'in' ? 'take' : 'give';
    const cutoff = Date.now() - 12 * 3_600_000;
    let pick: { at: string; asset: string } | null = null;
    for (const d of open.deals) {
      if (d.lifecycle !== 'awaiting' || !d.legs) continue;
      const leg = d.legs[side];
      if (leg.settled || !(leg.open > 0) || !sameAsset(leg.asset, asset)) continue;
      const t = Date.parse(d.booked_at);
      if (!Number.isFinite(t) || t > cutoff) continue;
      if (backdateProblem(isoToDubaiWall(d.booked_at))) continue;
      if (!pick || t < Date.parse(pick.at)) pick = { at: d.booked_at, asset: leg.asset };
    }
    return pick && { ...pick, name: partyName(selectedParty) };
  }, [selectedParty, slug, when, direction, asset, settlements.data, settlementsRead]);

  // ── submit ─────────────────────────────────────────────────────────────
  // The repeat guard's panel is shared with Move money and Inter-entity —
  // every screen that posts a transfer meets the same 409.
  const { twin: repeat, clearTwin, guard } = useRepeatGuard();

  // THE REFERENCE IS THE IDEMPOTENCY KEY, SO IT MUST SURVIVE A RETRY (review,
  // 5 Sept 2026 — the lesson NewTrade's deal id learned, arriving here late).
  // It was minted inside post(), which runs on EVERY submit: 230,000 AED
  // recorded as of 3 Sept, a timeout after the BFF had posted, Record pressed
  // again — and the second attempt carried a fresh RCPT-2026-09-03-… that the
  // ledger could not recognise as the same money, so it posted twice. Held in
  // a ref, not state: it must not re-render, and it must not reset when the
  // operator edits a field mid-retry. Cleared on success, and by
  // retireReference() when the prefix or the day it carries would change.
  const refRef = useRef<string | null>(null);
  /** Which refusals the operator has already answered for THIS entry (the
   *  Swap form's rule, 14 Sept 2026): the twin guard (confirm_repeat), the
   *  settled-trade guard (confirm_money_moved_again — the transfers-side
   *  mirror of Book trade's cross-shape guard) and the new-rail door
   *  (allow_new_rail_asset). The BFF asks them in that order on every POST,
   *  so a re-POST has to carry every yes given so far or an earlier door
   *  refuses again. Cleared with the reference: a new entry answers afresh. */
  const confirmedRef = useRef<Set<RepeatKind | 'new_rail'>>(new Set());

  // The account's name as the picker shows it — for the success line and the
  // new-account panel. A row the list does not carry (a prefill from a link)
  // still reads through railName, so a raw slug never reaches the screen.
  const railLabel = railName(
    (treasuryRows.data?.rows ?? []).find((r) => r.slug === counterRail.slug && sameAsset(r.asset, counterRail.asset))
      ?? { slug: counterRail.slug },
  );

  const post = async () => {
      // THE PARTY MUST HAVE BEEN READ: its TYPE picks the account the leg posts to (partyPath), and a party that has not been read — a search
      // still on its way, a failed one, a name the books do not hold — would be posted as a customer: a merchant's money on a phantom
      // customer account (Screens' Astra review, 23 Oct 2026). Every door that books comes through here, the button's own gate included.
      if (!selectedParty || lookup !== 'known') throw new Error('The party has not been read — wait for the list, or pick one from it.');
      // A movement involving a PARTY has two sides and must post both: the
      // party's balance AND the rail the money physically moved through.
      // Before 2026-08-13 this screen posted only one — inflow/outflow send to
      // @world, so "customer deposits 10,000 cash" either credited the customer
      // with no cash arriving anywhere, or banked the cash without recording
      // that the desk now owes them. Every such entry was half a transaction.
      // LEDGER-STANDARD called this out; transfer/transfer_party already had
      // the right shape.
      //
      // Party mode ALWAYS has a party on the other side (External retired
      // 2 Sept), so this is the only posting left. The treasury-only receipt
      // and payment branches that used to follow were unreachable — nothing
      // set partyKind — and were removed on 5 Sept 2026.
      const party = partyPath(slug, selectedPartyType);
      const rail  = treasuryPath(counterRail.slug, asset, railDesk);   // the account's own desk
      // The reference carries the day the money MOVED (RCPT-2026-09-03-… for
      // 3 Sept money typed on the 5th), like the deal id does — minted once
      // per entry, see refRef.
      refRef.current ??= makeReference(direction === 'in' ? 'RCPT' : 'PAY', whenIso || undefined);
      const reference = refRef.current;
      const data = await api.transfer({
        // in  = the party pays the desk: their balance falls, the rail rises
        // out = the desk pays the party: the rail falls, their balance rises
        source:      direction === 'in' ? party : rail,
        destination: direction === 'in' ? rail  : party,
        // Out with a cost: the account pays amount + cost, the party gets the
        // amount (see railDebitN).
        amount:      { asset, amount: direction === 'out' && costN > 0 ? railDebitN : Number(amount) },
        fee_payer:   'house',
        reference,
        // The Reference box that used to ride in here went on 14 Sept 2026
        // (§5.5: 2 of 72 uses, both names) — Notes is the one free-text box.
        notes:       notes.trim() || undefined,
        confirm_repeat: confirmedRef.current.has('repeat') || undefined,
        // The operator said yes to opening this asset on this rail (see the
        // new-rail panel below).
        allow_new_rail_asset: confirmedRef.current.has('new_rail') || undefined,
        // Only when set — absent means now (5 Sept 2026, see `when`).
        effective_at: whenIso || undefined,
        // A commission or courier charge inside an outgoing payment is a cost
        // to the desk, not money the party keeps.
        cost: direction === 'out' && costN > 0
          ? { amount: { asset, amount: costN }, category: costCategory }
          : undefined,
        // "This money really moved a second time" — the transfers-side guard's
        // own yes (14 Sept 2026; TransferBody names it since the same evening).
        confirm_money_moved_again: confirmedRef.current.has('money') || undefined,
      });
      // WHAT WAS SENT RIDES BACK WITH THE ANSWER, so the success line reports
      // the instant and reference that were posted — never whatever the
      // fields hold by the time it renders (see `posted`) — and the entry's
      // facts ride with it for the money-without-a-trade line.
      return {
        data, reference, asOf: whenIso || null,
        slug, merchant: selectedPartyType === 'counterparty', asset, direction, amount, rail: counterRail.slug,
        partyLabel: selectedParty ? partyName(selectedParty) : slug, railLabel,
      };
  };
  const submit = useMutation({
    // REPEAT GUARD (BFF 409 {error:'repeat_suspected', twin}): the same money
    // is already in the books. Name the twin, let the operator decide. The
    // confirmations a re-POST carries live in confirmedRef, not in the call.
    mutationFn: () => guard(post),
    onSuccess: ({ data, reference, asOf, ...facts }) => {
      clearTwin();
      refRef.current = null;
      confirmedRef.current.clear();
      // A deduped answer means the ledger already held this reference — a
      // first attempt whose response was lost — and kept ITS date; the date
      // in this request was ignored, so it must not be reported as posted.
      setPosted({ at: Date.now(), asOf, reference: data.reference ?? reference, deduped: !!data.deduped, ...facts });
      // The figures leave with the entry they were typed for (§7 rule 12,
      // 14 Sept 2026): the date rode into the next movement before 5 Sept,
      // and an amount left in the box is a second tap away from posting
      // twice. The party, the direction and the account stay — the next
      // entry is usually the same party's.
      setWhen(''); setAmount(''); setCostAmount(''); setNotes('');
      setReview(false);
      tg()?.HapticFeedback.notificationOccurred('success');
      // Booked FROM a chain row (?note=chain …): fire the matcher NOW so the
      // pending row is gone before the operator looks back (operator, 2 Sept:
      // "make it auto vanish once trade booked and matched"). Fire-and-forget —
      // a failed sync just leaves the row for the screen's own auto-sync.
      if ((search.get('note') ?? '').startsWith('chain ')) {
        api.chainSync().catch(() => {});
      }
    },
    onError:   (e) => { if (!isSentinel(e)) tg()?.HapticFeedback.notificationOccurred('error'); },
  });

  /** Drop the held reference so the next post mints its own — the prefix says
   *  the direction and the stamp says the day, so either changing makes the
   *  old one a lie. UNLESS the books may already hold it: a posting in flight,
   *  or a last failure that was not provably answered before the ledger write
   *  (lib/errors.ts nothingLanded — a timeout or 5xx can come back with the
   *  transfer posted). There a fresh reference is how the money posts twice,
   *  and the kept one makes the retry a no-op the BFF answers `deduped`. */
  const retireReference = () => {
    const mayHold = submit.isPending || (submit.isError && !nothingLanded(submit.error));
    if (!mayHold) { refRef.current = null; confirmedRef.current.clear(); }
  };

  // A READ THAT FAILED IS NOT AN EMPTY ONE (the party list, the rail list and the party's box each drew nothing, or a
  // "square", and said nothing). One line: what failed and the way out; the books' own sentence behind a tap.
  const retry = (again: () => unknown) => <button type="button" className="btn-link text-meta" onClick={() => void again()}>Retry</button>;

  // ── v2 LAYOUT (below this line only what is drawn changed) ─────────────────────────────────────────────────────
  const title = mode === 'customer' || mode === 'p2p' || mode === 'profit_share' || mode === 'charge'
    ? MODE_TITLE[mode] : RECORD_KIND_COPY[mode].title;

  // The weekly modes. Between parties and Profit share are the other builder's v2 forms, which draw their own card and
  // Astra's aside (About this job included), so the page adds only the head. Delivery charge is today's RecordCharge
  // (Mechanisms' submit path, unchanged) in the form column, its About beside it from lg. Keyed exactly as RecordMoney
  // keys them.
  if (mode === 'p2p' || mode === 'profit_share') {
    return (
      <>
        <PageHead title={title} />
        {mode === 'p2p' ? <BetweenPartiesV2 key={`p2p-${search.get('from') ?? ''}-${search.get('to') ?? ''}`} /> : <ProfitShareV2 />}
      </>
    );
  }
  if (mode === 'charge') {
    return (
      <>
        <PageHead title={title} />
        <FormPage
          bare
          form={<RecordCharge key={`chg-${search.get('party') ?? ''}-${search.get('deal') ?? ''}-${search.get('note') ?? ''}`} />}
          aside={<AboutFold help={<About mode={mode} />} />}
        />
      </>
    );
  }
  if (mode !== 'customer') {
    return (
      <>
        <PageHead title={title} />
        <OwnerFormV2 key={mode} kind={mode} />
      </>
    );
  }

  // The post's own disabled-until rule, verbatim (RecordMoney's button) — the Review button and the confirm both read it.
  const postDisabled = submit.isPending || !amountOk(amount) || !slug
    // The party read and matched — its type decides the account (post() refuses otherwise).
    || !partyExists || lookup !== 'known'
    // A cost box that is not a plain number would be dropped in silence (only while that box is on screen).
    || (direction === 'out' && costWords !== null)
    // A party movement without its rail would post half an entry.
    || !counterRail.slug
    // A back-date the BFF would refuse is refused here first.
    || !!whenWords;
  // Why the button is off, in a few words (the first unmet condition of the rule above).
  const why = submit.isPending ? 'Booking…'
    : !slug ? 'Pick a party.'
    : lookup === 'failed' ? 'Parties not read.'
    : lookup !== 'known' ? 'Reading parties…'
    : !partyExists ? 'Pick a party.'
    : !amountOk(amount) ? 'Enter an amount.'
    : !counterRail.slug ? 'Pick an account.'
    : direction === 'out' && costWords !== null ? 'Check the fee.'
    : whenWords ? 'Check the date.'
    : null;

  const lastTenTitle = direction === 'in'
    ? (selectedParty ? `Last 10 receipts from ${partyName(selectedParty)}` : 'Last 10 receipts')
    : (selectedParty ? `Last 10 payments to ${partyName(selectedParty)}` : 'Last 10 payments');
  // The context — the desk, when the balance was read — sits in About this job's fold (Astra, 1 Nov 2026).
  const facts = [
    { label: 'Desk', value: DESK_LABEL[railDesk] },
    { label: 'Read', value: box === 'known' && partyBalances.dataUpdatedAt ? fmtTime(partyBalances.dataUpdatedAt) : '—' },
  ];

  // The party's box before → after — under the party on the phone, in the side column from lg. Drawn only from a read
  // that succeeded (a failed one says so with a Retry; one on its way says it is checking).
  const balance = partyExists && (
    box === 'failed'
      ? <StateLine tone="warn" className="mt-1" more={readWords(partyBalances.error)} action={retry(partyBalances.refetch)}>Couldn&rsquo;t read the balance — try again.</StateLine>
    : box === 'waiting'
      ? <p className="mt-1 text-meta text-tg-hint">Checking balance…</p>
      : <BalanceRows lines={[{ asset, now: partyNow, after: amountN > 0 ? partyNext : undefined }]} />
  );

  const form = (
    <>
      {/* Two reads down is one line, naming both. */}
      {(lookup === 'failed' || accounts === 'failed') && (
        <StateLine
          tone="warn"
          more={readWords(lookup === 'failed' ? parties.error : treasuryRows.error)}
          action={retry(() => { if (lookup === 'failed') void parties.refetch(); if (accounts === 'failed') void treasuryRows.refetch(); })}
        >
          {`Couldn’t read ${lookup === 'failed' && accounts === 'failed' ? 'parties and accounts' : lookup === 'failed' ? 'parties' : 'the accounts'} — try again.`}
        </StateLine>
      )}
      {listBalancesFlagged && (
        <StateLine tone="warn" action={retry(parties.refetch)}>
          Balances not read — try again.
        </StateLine>
      )}

      {/* ON REVIEW THE FIELDS GIVE WAY to the readback (1 Nov 2026 audit); Edit brings them back, as typed. */}
      {!review && (<>
      {/* Direction — Money in stays the default; the readback and the button both carry it. */}
      <div className="flex gap-1 rounded-lg border border-stroke bg-panel-2 p-1">
        {([
          { v: 'in',  l: 'Money in',  Icon: ArrowDownLeft },
          { v: 'out', l: 'Money out', Icon: ArrowUpRight },
        ] as const).map((d) => (
          <button
            key={d.v}
            type="button"
            aria-pressed={direction === d.v}
            // The prefix (RCPT / PAY) follows the direction — see refRef.
            onClick={() => { setDirection(d.v); retireReference(); }}
            className={`inline-flex min-h-11 flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md px-3 text-body lg:min-h-10 ${direction === d.v ? 'border border-stroke bg-panel font-medium text-tg-text' : 'text-tg-hint hover:text-tg-text'}`}
          >
            <d.Icon className="h-4 w-4" aria-hidden />{d.l}
          </button>
        ))}
      </div>

      {/* Which desk's accounts the picker lists — draws only for an admin who sees both desks. */}
      <DeskSwitch value={railDesk} onChange={(d) => { setRailDesk(d); setCounterRail({ slug: '', asset: '' }); }} />

      {/* Astra's grid: Party | Amount, Asset | Account from lg; on the phone the party and the account take the row. */}
      <div className="grid grid-cols-2 gap-x-3 gap-y-4 lg:items-start lg:gap-x-4">
        <Field label="Party" className="col-span-2 lg:col-span-1">
          <Combobox
            value={slug}
            onChange={setSlug}
            options={partyOptions}
            more={rosterCut(parties.data)}
            placeholder="Search party"
            loading={slug.length > 0 && lookup === 'waiting'}
            emptyHint={slug.length === 0 ? null : lookup === 'failed' ? 'Parties could not be read.' : undefined}
          />
          {!partyExists && lookup === 'known' && slug.trim() !== '' && (
            <p className="mt-1 text-meta text-tg-hint">Pick a party from the list.</p>
          )}
          <div className="xl:hidden">{balance}</div>
        </Field>

        <Field label="Amount">
          <input className="input" value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" />
          <ErrorLine>{amountWords}</ErrorLine>
        </Field>

        <Field label="Asset">
          <select
            className={`select w-full ${assetMismatch ? 'border-neg' : ''}`}
            value={asset}
            onChange={(e) => setAsset(e.target.value as AssetCode)}
          >
            {assetChoices.map((a) => <option key={a} value={a}>{displayAsset(a)}</option>)}
          </select>
          {assetMismatch && (
            <ErrorLine>
              That account holds {railAssets.map(displayAsset).join(' / ')}, not {displayAsset(asset)}.
            </ErrorLine>
          )}
        </Field>

        {/* The second side of the entry: the pocket the money moved through. Required. */}
        <Field label="Account" className="col-span-2 lg:col-span-1">
          {accountsDown ? <AccountsDown value={counterRail} /> : (
          <RailPicker balanceBelow
            value={counterRail}
            onChange={(v) => {
              setCounterRail(v);
              // The picker hands back the rail's asset. Take it.
              if (v.asset) setAsset(v.asset as AssetCode);
            }}
            rows={treasuryRows.data?.rows ?? []}
            partySlug={partyExists ? slug : undefined}
            loading={treasuryRows.isFetching || accounts === 'waiting'}
            readFailed={accounts === 'failed'}
            placeholder={direction === 'in' ? 'Receiving account' : 'Paying account'}
          />
          )}
        </Field>
      </div>

      {/* MORE — As of, the fee on top (money out) and Notes. Open on arrival when a link brought one of them. */}
      <div className="border-t border-stroke pt-3">
        <MoreDetails label="More" defaultOpen={!!urlWhen || costAmount !== '' || notes !== ''}>
          <AsOfChips preset={urlWhen || undefined}
            value={when}
            // The reference carries the day — a new day, a new reference, unless the old one may already be in the
            // ledger (retireReference).
            onChange={(w) => { setWhen(w); retireReference(); }}
            disabled={submit.isPending}
          />
          {direction === 'out' && (
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 lg:items-start">
              <Field label="Fee on top, our cost" hint="optional">
                <div className="flex gap-2">
                  <input className="input flex-1" value={costAmount} onChange={(e) => setCostAmount(e.target.value)} placeholder="0" inputMode="decimal" />
                  <span className="self-center text-meta text-tg-hint">{displayAsset(asset)}</span>
                </div>
                <ErrorLine>{costWords}</ErrorLine>
              </Field>
              {costN > 0 && (
                <Field label="Cost category">
                  <select className="select w-full" value={costCategory} onChange={(e) => setCostCategory(e.target.value)}>
                    {EXPENSE_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </Field>
              )}
            </div>
          )}
          <Field label="Notes">
            <textarea className="input h-16" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything to remember" />
          </Field>
        </MoreDetails>
      </div>
      </>)}

      {/* The open-trades read, and the dating line: a settle-later trade of this party's still open in this asset and
          older than 12 h while As of says Now. The tap sets As of to the trade's instant. */}
      {settlementsWanted && (settlementsRead === 'failed' ? (
        <StateLine tone="warn" action={retry(settlements.refetch)}>
          Open trades not read — try again.
        </StateLine>
      ) : settlementsRead === 'waiting' && (
        <StateLine>Reading open trades…</StateLine>
      ))}
      {/* A warning looks like one (1 Nov 2026 audit, P5): the kit's notice box, its one action at the right. */}
      {dating && (
        <Notice
          tone="warn"
          action={
            <button
              type="button"
              className="btn-secondary min-h-[44px] px-3 text-body lg:min-h-[40px]"
              disabled={submit.isPending}
              onClick={() => { setWhen(isoToDubaiWall(dating.at)); retireReference(); }}
            >
              Set to {fmtDay(dating.at)}
            </button>
          }
        >
          <span title="If this is its money, set when it moved.">Open settle-later trade · {fmtDay(dating.at)} · {displayAsset(dating.asset)}</span>
        </Notice>
      )}

      {review ? (
        <ReviewPanel
          onEdit={() => setReview(false)}
          // The party's balance read lives in the Party field, which Review puts away: its failure is said here too.
          notices={partyExists && box === 'failed' ? balance : null}
          why={postDisabled ? why : null}
          rows={[
            { label: 'Direction', value: direction === 'in' ? 'Money in' : 'Money out' },
            { label: 'Party', value: selectedParty ? partyName(selectedParty) : slug },
            { label: 'Amount', value: amountOk(amount) ? <ReceiptAmount value={amount} asset={asset} /> : '—' },
            { label: 'Account', value: counterRail.slug ? railLabel : '—' },
            // EVERY FIELD THE POSTING CARRIES IS READ BACK (Astra, 1 Nov 2026: the fee's category posted unseen) — one per row.
            direction === 'out' && costN > 0 && amountN > 0 && { label: 'Fee on top', value: <ReceiptAmount value={costN} asset={asset} /> },
            direction === 'out' && costN > 0 && amountN > 0 && { label: 'Fee category', value: EXPENSE_CATEGORIES.find((c) => c.value === costCategory)?.label ?? costCategory },
            direction === 'out' && costN > 0 && amountN > 0 && { label: 'Leaves the account', value: <ReceiptAmount value={railDebitN} asset={asset} /> },
            { label: 'As of', value: whenIso && !whenWords ? fmtDay(whenIso) : 'Now' },
            { label: 'Balance', value: box === 'known' ? <BalanceRows className="!mt-0" lines={[{ asset, now: partyNow, after: amountN > 0 ? partyNext : undefined }]} /> : '—' },
            notes.trim() !== '' && { label: 'Notes', value: notes.trim() },
          ]}
          confirm={
            // While a repeat panel is open the button drops to the secondary style, so the panel's own remedy is the
            // one gold button. A restyle only — nothing is disabled.
            <button
              className={`${repeat ? 'btn-secondary' : 'btn-primary'} w-full lg:w-auto lg:px-6`}
              disabled={postDisabled}
              onClick={() => submit.mutate()}
            >
              {submit.isPending ? 'Booking…'
                : `${direction === 'in' ? 'Record money in' : 'Record money out'}${whenIso && !whenWords ? ` as of ${fmtDay(whenIso)}` : ''}`}
            </button>
          }
        />
      ) : (
        <FormFoot line={why}>
          <ReviewButton label="Review entry" disabled={postDisabled} onClick={() => setReview(true)} />
        </FormFoot>
      )}

      {/* words:ok — the books' own sentence; a 5xx or a lost answer says it may have posted (ServerLine). */}
      {submit.isError && !isSentinel(submit.error) && !isNewRailRefusal(submit.error) && <ServerLine error={submit.error} />}
      {/* AN ACCOUNT THAT HAS NEVER HELD THIS ASSET: one line, what it holds behind Details, and the yes on a button. */}
      {submit.isError && isNewRailRefusal(submit.error) && (
        <div className="space-y-2">
          <StateLine
            tone="warn"
            more={`${newRailHolds(submit.error).length ? `Holds ${newRailHolds(submit.error).map(displayAsset).join(' / ')} today` : 'Never posted to'} — check asset and account.`}
          >
            That account has never held {displayAsset(asset)}.
          </StateLine>
          <button
            type="button"
            className="btn-secondary w-full text-body lg:w-auto"
            disabled={submit.isPending || !!whenWords}
            onClick={() => { confirmedRef.current.add('new_rail'); submit.mutate(); }}
          >
            Yes — open {displayAsset(asset)} on {railLabel} and post
          </button>
        </div>
      )}
      {repeat && (
        <RepeatPanel
          twin={repeat}
          // A back-date the BFF would refuse locks this door as well.
          pending={submit.isPending || !!whenWords}
          onAlreadyBooked={() => { clearTwin(); nav(-1); }}
          onPostAnyway={() => { confirmedRef.current.add(repeat.kind); submit.mutate(); }}
          settleLater={{ to: tradeHref({ party: slug, direction, asset, amount, when, rail: counterRail.slug, later: true }) }}
        />
      )}
      <LastTenLink {...lastTen} title={lastTenTitle} />
      {/* The phone's way to the job's explanation (the aside is laptop-only): behind a tap, as there (restored 1 Nov 2026). */}
      <AboutFold help={<About mode={mode} />} facts={facts} className="xl:hidden" />
    </>
  );

  /* THE RECEIPT IN THE FORM'S PLACE (his "screen UI and confirmation msgs!!!!", 1 Nov 2026): what POSTED, row by row —
     never the live fields — with the reference, "Record another" back to a fresh form (party and account stay), the
     recent entries, and the Book-the-trade line when the coins have no trade behind them. */
  const receipt = submit.isSuccess && posted ? (
    <Receipt
      title={posted.direction === 'in' ? 'Money in recorded' : 'Money out recorded'}
      deduped={posted.deduped}
      reference={posted.reference}
      facts={[
        { label: posted.direction === 'in' ? 'Received' : 'Paid', value: <ReceiptAmount value={posted.amount} asset={posted.asset} /> },
        { label: posted.direction === 'in' ? 'From' : 'To', value: posted.partyLabel },
        { label: posted.direction === 'in' ? 'Into' : 'Out of', value: posted.railLabel },
        { label: 'As of', value: postedAsOf(posted.asOf, fmtDay, submit.submittedAt) },
      ]}
      another={{ label: 'Record another', onClick: () => submit.reset() }}
      movements={`/movements?kind=${posted.direction === 'in' ? 'money_in' : 'money_out'}&party=${encodeURIComponent(posted.slug)}`}
      links={!posted.deduped && (
        <Link to={`/movements?kind=${posted.direction === 'in' ? 'money_in' : 'money_out'}&party=${encodeURIComponent(posted.slug)}`} className={TAP}>
          Recent entries →
        </Link>
      )}
      extra={nudge && posted && (
        <StateLine
          tone="warn"
          action={
            <Link
              to={tradeHref({
                party: posted.slug, direction: posted.direction, asset: posted.asset, amount: posted.amount,
                when: posted.asOf ? isoToDubaiWall(posted.asOf) : '', rail: posted.rail, later: true,
              })}
              className="btn-link text-meta"
            >
              Book the trade →
            </Link>
          }
        >
          <span className="whitespace-nowrap">{boxWords(nudge.held, posted.asset).replace(/^./, (c) => c.toUpperCase())} {displayAsset(foldAsset(posted.asset))}</span> — no trade behind it.
        </StateLine>
      )}
    />
  ) : null;

  // After a posting the receipt says it all: the aside keeps only the Last 10, the rest is for before the confirm (Astra).
  const lastTenList = (
    <LastTen {...lastTen} title={lastTenTitle} />
  );
  const aside = receipt ? lastTenList : (
    <>
      <AsideBlock title="Party balance">
        {partyExists ? balance : <p className="text-meta text-tg-hint">Pick a party.</p>}
      </AsideBlock>
      {lastTenList}
      <AboutFold help={<About mode={mode} />} facts={facts} />
    </>
  );

  return (
    <>
      <PageHead title={title} />
      <FormPage form={receipt ?? form} aside={aside} />
    </>
  );
}

/** The BFF's "this rail has never held this asset" refusal (400 new_rail_asset). */
function isNewRailRefusal(e: unknown): boolean {
  return e instanceof ApiError && e.code === 'new_rail_asset';
}
/** What that rail holds today, from the refusal's own body. */
function newRailHolds(e: unknown): string[] {
  const holds = (e as ApiError)?.body?.holds;
  return Array.isArray(holds) ? holds.map(String) : [];
}
